const findDuplicates = (question, field = '') => {
  const encounteredValues = {};

  for (const option of question.options) {
    const fieldValue = typeof option[field] === 'string' ? option[field].trim() : option[field];

    if (encounteredValues[fieldValue]) {
      const duplicatedOptions = question.options.filter((opt) =>
        typeof opt[field] === 'string' ? opt[field].trim() : opt[field] === fieldValue
      );

      return {
        questionId: question.id,
        options: duplicatedOptions.map((item) => ({
          ...item,
          error: { [field]: 'Same field' }
        }))
      };
    } else {
      encounteredValues[fieldValue] = true;
    }
  }
};

const validateNumericOptions = (question) => {
  if (!question.options.every((option) => !isNaN(option.value))) {
    return {
      questionId: question.id,
      options: question?.options
        .filter((option) => isNaN(option.value))
        .map((option) => ({
          ...option,
          error: { value: 'Enter a numeric value' }
        }))
    };
  }

  return null;
};

const validateBooleanOptions = (question) => {
  if (
    question.options.length !== 2 ||
    !question.options.every((option) => typeof option.value === 'boolean')
  ) {
    return {
      questionId: question.id,
      options: question?.options
        .filter((option) => typeof option.value != 'boolean')
        .map((option) => ({
          ...option,
          error: { value: 'Must have boolean values' }
        }))
    };
  }

  return null;
};

const validateEmptyFields = (question) => {
  const invalidOptions = question.options.filter((option) => option.value === '' || option.option === '');

  if (invalidOptions.length > 0) {
    return {
      questionId: question.id,
      options: invalidOptions.map((option) => ({
        ...option,
        error: {
          value: option.value === '' ? 'Missing required value' : '',
          option: option.option === '' ? 'Missing required value' : ''
        }
      }))
    };
  }
  return null;
};

const validateVariant = (question) => {
  if (!question.variant || !question.variant?.value) {
    return {
      variant: 'Missing required variant',
      questionId: question.id
    };
  }
};

const validateRange = (question) => {
  const { config = {} } = question;
  const { min, max, skip } = config?.range || {};

  if (isNaN(min) || Number(min) < 0 || min === '') {
    return {
      minRange: 'Missing required value',
      questionId: question?.id
    };
  }

  if (isNaN(max) || Number(max) < 0 || max === '') {
    return {
      maxRange: 'Missing required value',
      questionId: question?.id
    };
  }

  if (Number(max) < 1) {
    return {
      maxRange: 'Must be greater than 0',
      questionId: question?.id
    };
  }

  if (Number(max) <= Number(min)) {
    return {
      maxRange: `Must be greater than ${min}`,
      questionId: question?.id
    };
  }

  if (Number(skip) >= Number(max)) {
    return {
      skipRange: `Must be lower than ${max}`,
      questionId: question?.id
    };
  }

  if (!skip || isNaN(skip) || Number(skip) <= 0) {
    return {
      skipRange: 'Missing required value',
      questionId: question?.id
    };
  }
};

const isValidUrl = (url) => {
  try {
    if (!url || url.trim() === '') {
      return false;
    }

    const parsedUrl = new URL(url.trim());

    if (!parsedUrl.protocol.startsWith('http')) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

const validateImageOptions = (question) => {
  const invalidOptions = question.options.filter((option) => {
    return !option.image || !isValidUrl(option.image);
  });

  if (invalidOptions.length > 0) {
    return {
      questionId: question.id,
      options: invalidOptions.map((option) => ({
        ...option,
        error: {
          ...option.error,
          image: !option?.image || option?.image?.trim() === '' ? 'Image is required' : ''
        }
      }))
    };
  }

  return null;
};

const validateQuestions = (questions) => {
  let requiredFields = {};

  for (let index = 0; index < questions.length; index++) {
    const question = questions[index];

    if (question?.type?.value !== 'groupreference' && !question.title) {
      requiredFields = { ...requiredFields, title: 'Missing required title' };
    }

    if (question?.type?.value === 'groupreference' && !question.markdown) {
      requiredFields = { ...requiredFields, content: 'Missing required content' };
    }

    if (!question.type || !question.type?.value) {
      requiredFields = { ...requiredFields, type: 'Missing required type' };
    }

    if (Object.keys(requiredFields).length > 0) {
      return { ...requiredFields, questionId: question.id };
    }

    // Check specific rules based on question type
    switch (question.type.value) {
      case 'singlechoice':
      case 'multiplechoice': {
        //Making sure a variant is selected
        const invalidVariant = validateVariant(question);
        if (invalidVariant) {
          return invalidVariant;
        }

        // For string and numeric types, ensure option values are not empty
        const invalidOptions = validateEmptyFields(question);
        if (invalidOptions) {
          return invalidOptions;
        }

        // Look for duplicated values
        const foundDuplicatedValues = findDuplicates(question, 'value');
        if (foundDuplicatedValues) {
          return foundDuplicatedValues;
        }

        // Look for duplicated options
        const foundDuplicatedOptions = findDuplicates(question, 'option');
        if (foundDuplicatedOptions) {
          return foundDuplicatedOptions;
        }

        // For numeric type, ensure option values are numbers
        if (question.variant.value === 'numeric') {
          const invalidNumericOptions = validateNumericOptions(question);

          if (invalidNumericOptions) {
            return invalidNumericOptions;
          }
        }

        //Validate boolean options for single choice questions
        if (question.type.value === 'singlechoice' && question.variant?.value === 'boolean') {
          const invalidBooleanOptions = validateBooleanOptions(question);
          if (invalidBooleanOptions) {
            return invalidBooleanOptions;
          }
        }

        //Validate range questions
        if (question.type.value === 'singlechoice' && question.variant?.value === 'range') {
          const invalidRange = validateRange(question);
          if (invalidRange) {
            return invalidRange;
          }
        }

        //Validate image questions
        if (question.type.value === 'singlechoice' && question.variant?.value === 'image') {
          const invalidImageOptions = validateImageOptions(question);
          if (invalidImageOptions) {
            return invalidImageOptions;
          }
        }

        break;
      }

      case 'ranking': {
        // Ensures option values are not empty
        const invalidOptions = validateEmptyFields(question);
        if (invalidOptions) {
          return invalidOptions;
        }

        // Look for duplicated values
        const foundDuplicatedValues = findDuplicates(question, 'value');
        if (foundDuplicatedValues) {
          return foundDuplicatedValues;
        }

        // Look for duplicated options
        const foundDuplicatedOptions = findDuplicates(question, 'option');
        if (foundDuplicatedOptions) {
          return foundDuplicatedOptions;
        }

        // Ensures option values are numbers
        const invalidNumericOptions = validateNumericOptions(question);
        if (invalidNumericOptions) {
          return invalidNumericOptions;
        }

        break;
      }
      default:
        // No additional validation for other question types
        break;
    }
  }

  return requiredFields;
};

export default validateQuestions;
